import React, { useCallback } from 'react';

import CheckboxFormField from '@components/form-fields/CheckboxFormField';
import Loader from '@components/loader/Loader';
import Error from '@mui/icons-material/Error';
import {
  lighten,
  TableBody,
  TableCell,
  TableRow,
  Tooltip
} from '@mui/material';
import { MAIN_THEME } from '@styles';
import { getRowConditions } from '@utils/table-utils';
import { isEmpty } from '@utils/validationUtils';

import { EnhancedTableHeader } from './widgets/shared-with-old';
import EnhancedTableBodyCell from './widgets/shared-with-old/EnhancedTableBodyCell';
import { IEnhancedRow } from './types';
import { ITableRowSettings } from '.';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

interface IListingTableBodyProps {
  disableSelection?: boolean | DisableSelectionType;
  emptyRows: number;
  handleClick(
    name: string,
    checked: boolean,
    rowIndex?: string,
    rowId?: string
  ): void;
  handleCellValueChanged?(
    rowKey: number,
    property: string,
    value: unknown,
    row?: any,
    rowName?: string
  ): void;
  handleBlur?(rowIndex: number, columnId: string, newValue: unknown): void;
  headers: Record<string, EnhancedTableHeader>;
  rowSettings?: ITableRowSettings;
  dynamicRowMessage?: (row: IEnhancedRow) => string[];
  isSelected: (key: string) => boolean;
  rows?: IEnhancedRow[];
  currencySymbol?: string;
  phoneNumberPattern?: string;
  entityName?: string;
  loader?: boolean;
  showCellFullData?: boolean;
  forceShowSelectColumn?: boolean;
  tableClasses?: {
    inputContainer?: string;
  };
}

export enum DisableSelectionType {
  'Hide' = 'Hide'
}

const useStyles = makeStyles()(() => ({
  bodyRoot: {
    '& tr:nth-of-type(odd)': {
      backgroundColor: MAIN_THEME.color.white98
    }
  },
  rowRoot: {
    '&.Mui-selected': {
      backgroundColor: lighten(MAIN_THEME.color.black, 0.85)
    },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: lighten(MAIN_THEME.color.black, 0.85)
    }
  },
  cellRoot: {
    color: `${MAIN_THEME.color.black} !important`,
    fontSize: `${MAIN_THEME.fontSize.sm}  !important`,
    lineHeight: '12px !important',
    height: '34px !important',
    border: 'none !important',
    padding: '4px 2px !important',
    fontFamily: MAIN_THEME.font.bold
  },
  checkboxCellRoot: {
    width: '14px !important',
    padding: '0 14px 0 8px !important',
    border: 'none !important',
    height: '34px !important',
    position: 'relative !important' as 'relative',
    fontFamily: MAIN_THEME.font.regular
  },
  checkboxCellRootWithMessage: {
    padding: '0 20px 0 8px !important'
  },
  noDataMessage: {
    textAlign: 'center',
    color: MAIN_THEME.color.white86
  },
  noDataCell: {
    color: MAIN_THEME.color.black,
    border: 'none',
    height: '34px',
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '12px',
    fontFamily: MAIN_THEME.font.regular
  },
  errorIconStyle: {
    height: '15px',
    width: '15px',
    color: 'red',
    position: 'absolute',
    top: '10px',
    right: '8px',
    cursor: 'pointer'
  },
  paddedCellRoot: {
    padding: '0px 6px 0px 20px !important'
  },
  selectablePaddedCellRoot: {
    padding: '0px 6px 0px 60px !important',
    fontSize: `${MAIN_THEME.fontSize.sm} !important`
  }
}));

const ListingTableBody: React.FC<IListingTableBodyProps> = ({
  emptyRows,
  handleClick,
  handleCellValueChanged,
  handleBlur,
  headers,
  isSelected,
  dynamicRowMessage,
  rowSettings,
  rows,
  currencySymbol,
  disableSelection,
  loader,
  showCellFullData,
  forceShowSelectColumn,
  tableClasses
}) => {
  const { classes } = useStyles();

  const checkIfRowHasMessage = useCallback(
    (row: IEnhancedRow) => {
      if (dynamicRowMessage) {
        const dynamcMessages = dynamicRowMessage(row);
        if (dynamcMessages && dynamcMessages.length > 0) {
          return true;
        }
      }

      if (
        isSelected(row.key) &&
        Number(row.columns.OutstandingAmount) <
          Number(row.columns.CollectedAmount)
      ) {
        return true;
      }
      if (
        !isSelected(row.key) ||
        !rowSettings ||
        !rowSettings.conditions ||
        rowSettings.conditions.length === 0
      ) {
        return false;
      }

      return getRowConditions(rowSettings, row);
    },

    [rows, rowSettings]
  );

  const renderRow = (row: IEnhancedRow) => {
    let message = '';
    if (dynamicRowMessage) {
      const dynamcMessages = dynamicRowMessage(row);
      if (dynamcMessages && dynamcMessages.length > 0) {
        message = dynamcMessages[0];
      }
    }

    if (!message) {
      if (rowSettings && checkIfRowHasMessage(row)) {
        message = rowSettings.iconTooltip;
      }
    }
    if (message) {
      return (
        <Tooltip title={message}>
          <span>
            <Error className={classes.errorIconStyle} />
          </span>
        </Tooltip>
      );
    }

    return <></>;
  };

  return (
    <TableBody
      classes={{
        root: classes.bodyRoot
      }}
    >
      {rows.length > 0 ? (
        rows.map((row, index) => {
          const isItemSelected = isSelected(row.key);
          const labelId = `enhanced-table-checkbox-${index}`;
          const rowMessage = checkIfRowHasMessage(row);

          return (
            <TableRow
              hover
              role="checkbox"
              aria-checked={isItemSelected}
              tabIndex={-1}
              key={row.key}
              selected={isItemSelected}
              classes={{ root: classes.rowRoot }}
            >
              {(!disableSelection || !isEmpty(rowMessage)) && (
                <TableCell
                  padding="none"
                  classes={{
                    root: clsx(classes.checkboxCellRoot)
                  }}
                >
                  {!disableSelection && (
                    <CheckboxFormField
                      checked={isItemSelected}
                      name={row.key}
                      onChange={handleClick}
                      inputOnly
                    />
                  )}
                  {renderRow(row)}
                </TableCell>
              )}
              {Object.values(headers).map((header, headerIndex) => {
                return (
                  <EnhancedTableBodyCell
                    selected={isSelected(row.key)}
                    key={header.name + labelId}
                    row={row}
                    header={header}
                    currencySymbol={currencySymbol}
                    className={clsx({
                      [classes.paddedCellRoot]:
                        disableSelection &&
                        forceShowSelectColumn &&
                        !headerIndex
                    })}
                    tableClasses={tableClasses}
                    showCellFullData={showCellFullData}
                    handleCellValueChanged={
                      handleCellValueChanged
                        ? (rk, col, val) =>
                            handleCellValueChanged(rk, col, val, row, row.key)
                        : undefined
                    }
                    handleUpdate={handleBlur}
                  />
                );
              })}
            </TableRow>
          );
        })
      ) : (
        <TableRow>
          <TableCell
            rowSpan={2}
            colSpan={
              Object.keys(headers).length > 0
                ? Object.keys(headers).length + 1
                : 1
            }
            align="center"
            classes={{ root: classes.noDataCell }}
          >
            {loader ? <Loader /> : 'No Data'}
          </TableCell>
        </TableRow>
      )}
      {emptyRows > 0 && <TableRow style={{ height: 34 * emptyRows }} />}
    </TableBody>
  );
};

export default ListingTableBody;
